import { useCallback, useEffect, useState } from "react";
import type { ChangeEmitter } from "@/data/repositories";

export interface QueryState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

const NO_SOURCES: readonly ChangeEmitter[] = [];

/**
 * Runs an async query on mount, whenever `deps` change and whenever one of
 * `sources` emits. `deps` must be JSON-serialisable; `sources` must be a
 * stable array (use `app.changeSources`).
 */
export function useQuery<T>(
  query: () => Promise<T>,
  deps: readonly unknown[],
  sources: readonly ChangeEmitter[] = NO_SOURCES,
): QueryState<T> {
  const [version, setVersion] = useState(0);
  const [result, setResult] = useState<{ key: string; data: T | undefined; error: Error | null }>({
    key: "", data: undefined, error: null,
  });
  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const key = JSON.stringify([version, ...deps]);

  useEffect(() => {
    const unsubscribes = sources.map((source) => source.subscribe(reload));
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [sources, reload]);

  useEffect(() => {
    let cancelled = false;
    query().then(
      (data) => {
        if (!cancelled) setResult({ key, data, error: null });
      },
      (cause: unknown) => {
        if (!cancelled) {
          const error = cause instanceof Error ? cause : new Error(String(cause));
          setResult((prev) => ({ key, data: prev.data, error }));
        }
      },
    );
    return () => {
      cancelled = true;
    };
    // The query closure is intentionally not a dependency; `key` captures deps + reload version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data: result.data, error: result.error, loading: result.key !== key, reload };
}
